import { decode, encode } from '@/helpers/param-encoder'
import { parse, stringify } from '@/helpers/qs'
import type {
    GemsApi_StoneFilters,
    GemsApi_StoneShape,
    GemsBoundsFragment,
    GemsRangeFragment,
    Scalars,
} from '@/graphql/api'
import { computed, type ComputedRef, type Ref, toValue } from 'vue'
import type { AppCurrency } from '@/plugins/currency'
import type { Bounds } from 'wucdbm-exponential-slider'

export function encodeFiltersAndStoneIdentifier(
    filters: GemsApi_StoneFilters | undefined,
    stoneIdentifier: string | undefined
): string | undefined {
    const fragment = {
        stone: maybeStoneIdentifier(stoneIdentifier),
        filters: encode(filters),
    }

    return Object.values(fragment).filter((v) => !!v).length
        ? `#${stringify(fragment)}`
        : undefined
}

export type DecodedStoneFiltersAndIdentifier = {
    filters: GemsApi_StoneFilters | undefined
    stoneIdentifier: string | undefined
}

export function decodeFiltersAndStoneIdentifier(
    fragment: string | undefined
): DecodedStoneFiltersAndIdentifier | undefined {
    if (!fragment) {
        return undefined
    }

    const parsed = parse(fragment)

    return {
        filters:
            typeof parsed['filters'] === 'string'
                ? decode(parsed['filters'])
                : undefined,
        stoneIdentifier:
            typeof parsed['stone'] === 'string' ? parsed['stone'] : undefined,
    }
}

export function isStoneIdentifier(identifier: string): boolean {
    return /^[A-Z\d]{7}$/.test(identifier)
}

export function maybeStoneIdentifier(
    identifier: string | undefined
): Scalars['GemsApi_StoneIdentifier']['input'] | undefined {
    if (identifier && isStoneIdentifier(identifier)) {
        return identifier
    }

    return undefined
}

export type PriceBounds = {
    bounds: Bounds
    currency: AppCurrency
}

export function usePriceBounds(
    bounds:
        | Ref<GemsBoundsFragment | undefined | null>
        | (() => GemsBoundsFragment | undefined | null),
    shape:
        | Ref<GemsApi_StoneShape | undefined>
        | (() => GemsApi_StoneShape | undefined),
    defaults: Bounds,
    defaultCurrency: AppCurrency
): ComputedRef<PriceBounds | undefined> {
    return computed((): PriceBounds | undefined => {
        const b = toValue(bounds)

        if (!b) {
            return undefined
        }

        const s = toValue(shape)

        const byShape = b.priceByShape.find((bs) => bs.shape === s)

        const range = byShape ? byShape.range : b.price

        const min = range.minInCurrency
        const max = range.maxInCurrency

        if (min && max) {
            const boundsOrDefaults = boundsIfNotZeroOrDefaults(
                {
                    min: Number(min?.amount || range.min),
                    max: Number(max?.amount || range.max),
                },
                defaults
            )

            return {
                bounds: boundsOrDefaults,
                currency:
                    boundsOrDefaults === defaults
                        ? defaultCurrency
                        : min.currency,
            }
        }

        return {
            bounds: defaults,
            currency: defaultCurrency,
        }
    })
}

export function useBounds(
    bounds: Ref<Bounds | undefined | null> | (() => Bounds | undefined | null),
    defaults: Bounds
): ComputedRef<Bounds> {
    return computed(() => {
        const b = toValue(bounds)

        if (!b) {
            return defaults
        }

        return boundsIfNotZeroOrDefaults(b, defaults)
    })
}

export function usePercentBounds(
    bounds:
        | Ref<GemsRangeFragment | undefined | null>
        | (() => GemsRangeFragment | undefined | null),
    defaults: Bounds
): ComputedRef<Bounds> {
    return computed(() => {
        const b = toValue(bounds)

        if (!b) {
            return defaults
        }

        const fa = boundsIfNotZeroOrDefaults(b, defaults)

        return {
            min: Math.floor(fa.min),
            max: Math.ceil(fa.max),
        }
    })
}

function boundsIfNotZeroOrDefaults(bounds: Bounds, defaults: Bounds): Bounds {
    if (0 === bounds.min && 0 === bounds.max) {
        return defaults
    }

    return bounds
}
